import React, { useState, useCallback } from "react";
import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  Phone,
  Navigation,
  Star,
  Clock,
  CheckCircle,
} from "lucide-react";

/* ================= TYPES ================= */

export interface DermatologistService {
  place_id: string;
  name: string;
  vicinity: string;
  rating: number;
  user_ratings_total: number;
  photos: { photo_reference: string }[];
  geometry: {
    location: { lat: number; lng: number };
  };
  business_status: string;
  opening_hours: { open_now: boolean };
  price_level: number;
  types: string[];
  distance: number;
}

export interface JobType {
  id: string;
  title: string;
  location?: string;
  description?: string;
  distance?: number;
  category?: string;
  jobData?: any;
}

/* ================= CONSTANTS - EXACT FROM REACT NATIVE ================= */

// Phone numbers map for dermatologist clinics
const PHONE_NUMBERS_MAP: { [key: string]: string } = {
  dermatologist_1: "08971562451",
  dermatologist_2: "08128730155",
  dermatologist_3: "07947411728",
  dermatologist_4: "08904598606",
  dermatologist_5: "09866447799",
};

// Export dummy dermatologist data - EXACT FROM REACT NATIVE
export const DUMMY_DERMATOLOGISTS: DermatologistService[] = [
  {
    place_id: "dermatologist_1",
    name: "Celestee Skin and Hair Clinic",
    vicinity: "Doctors Colony Saroor Nagar, Hyderabad",
    rating: 4.8,
    user_ratings_total: 48,
    photos: [{ photo_reference: "dermatologist_photo_1" }],
    geometry: {
      location: { lat: 17.385, lng: 78.4867 },
    },
    business_status: "OPERATIONAL",
    opening_hours: { open_now: true },
    price_level: 2,
    types: ["dermatologist", "health"],
    distance: 2.1,
  },
  {
    place_id: "dermatologist_2",
    name: "Dr.Madhuri's Skin Hair and Laser Clinic",
    vicinity: "Perraju Peta Bhanugudi Junction, Kakinada",
    rating: 4.8,
    user_ratings_total: 87,
    photos: [{ photo_reference: "dermatologist_photo_2" }],
    geometry: {
      location: { lat: 16.9891, lng: 82.2475 },
    },
    business_status: "OPERATIONAL",
    opening_hours: { open_now: true },
    price_level: 2,
    types: ["dermatologist", "health"],
    distance: 3.5,
  },
  {
    place_id: "dermatologist_3",
    name: "Dr. Vijetha Skin & Aesthetics",
    vicinity: "Balasamudram Hanamkonda, Warangal",
    rating: 4.7,
    user_ratings_total: 677,
    photos: [{ photo_reference: "dermatologist_photo_3" }],
    geometry: {
      location: { lat: 17.9689, lng: 79.5941 },
    },
    business_status: "OPERATIONAL",
    opening_hours: { open_now: true },
    price_level: 2,
    types: ["dermatologist", "health"],
    distance: 4.2,
  },
  {
    place_id: "dermatologist_4",
    name: "Layers Skin & Hair Clinics",
    vicinity: "4th Floor 16 11 762 Legend Rinda Capital Moosarambagh, Hyderabad",
    rating: 5.0,
    user_ratings_total: 1,
    photos: [{ photo_reference: "dermatologist_photo_4" }],
    geometry: {
      location: { lat: 17.385, lng: 78.4867 },
    },
    business_status: "OPERATIONAL",
    opening_hours: { open_now: true },
    price_level: 2,
    types: ["dermatologist", "health"],
    distance: 1.9,
  },
  {
    place_id: "dermatologist_5",
    name: "Skin & Hair Clinic",
    vicinity: "Gandhi Nagar, Vijayawada",
    rating: 4.6,
    user_ratings_total: 234,
    photos: [{ photo_reference: "dermatologist_photo_5" }],
    geometry: {
      location: { lat: 16.508, lng: 80.649 },
    },
    business_status: "OPERATIONAL",
    opening_hours: { open_now: true },
    price_level: 2,
    types: ["dermatologist", "health"],
    distance: 2.7,
  },
];

// Dermatologist clinic images
const DERMATOLOGIST_IMAGES_MAP: { [key: string]: string[] } = {
  dermatologist_1: [
    "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800",
    "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800",
    "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=800",
  ],
  dermatologist_2: [
    "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800",
    "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800",
    "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=800",
  ],
  dermatologist_3: [
    "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=800",
    "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800",
    "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800",
  ],
  dermatologist_4: [
    "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800",
    "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800",
    "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=800",
  ],
  dermatologist_5: [
    "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800",
    "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800",
    "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=800",
  ],
};

// Dermatologist clinic descriptions
const DERMATOLOGIST_DESCRIPTIONS_MAP: { [key: string]: string } = {
  dermatologist_1:
    "Excellence in Skin And Hair Innovation. Verified clinic with 4.8★ rating and 48 reviews. Trust badge. Responsive service. Available Now. Specializing in comprehensive dermatology care.",
  dermatologist_2:
    "Trending clinic with 4.8★ rating and 87 reviews. 4 Years in Healthcare. Available Now. Expert dermatologist specializing in skin, hair, and laser treatments.",
  dermatologist_3:
    'Popular verified clinic with 4.7★ rating and 677 reviews. 21 Years in Healthcare. "Excellent treatment" - 6 Suggestions. Comprehensive skin and aesthetics services.',
  dermatologist_4:
    "Premium clinic with perfect 5.0★ rating. Available Now. Specializing in advanced skin and hair treatments with modern facilities.",
  dermatologist_5:
    "Established clinic with 4.6★ rating and 234 reviews. Available Now. Offering complete dermatology solutions for skin and hair.",
};

// Dermatologist services offered
const DERMATOLOGIST_SERVICES = [
  "Acne Treatment",
  "Hair Loss Treatment",
  "Skin Allergy",
  "Laser Treatment",
  "Anti-Aging",
  "Chemical Peels",
  "Mole Removal",
  "Pigmentation",
];

/* ================= COMPONENT ================= */

interface NearbyDermatologistsCardProps {
  job?: JobType;
  onViewDetails: (job: JobType) => void;
}

// Single Dermatologist Card Component
const SingleDermatologistCard: React.FC<{
  job: JobType;
  onViewDetails: (job: JobType) => void;
}> = ({ job, onViewDetails }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageError, setImageError] = useState(false);

  // Get all available photos from job data - Using useCallback like React Native
  const getPhotos = useCallback(() => {
    if (!job) return [];
    const jobId = job.id || "dermatologist_1";
    return (
      DERMATOLOGIST_IMAGES_MAP[jobId] || DERMATOLOGIST_IMAGES_MAP["dermatologist_1"]
    );
  }, [job]);

  const photos = getPhotos();
  const hasPhotos = photos.length > 0;
  const currentPhoto = hasPhotos ? photos[currentImageIndex] : null;

  // Navigate to previous image - Using useCallback like React Native
  const handlePrevImage = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (hasPhotos && currentImageIndex > 0) {
        setCurrentImageIndex((prev) => prev - 1);
      }
    },
    [hasPhotos, currentImageIndex]
  );

  // Navigate to next image - Using useCallback like React Native
  const handleNextImage = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (hasPhotos && currentImageIndex < photos.length - 1) {
        setCurrentImageIndex((prev) => prev + 1);
      }
    },
    [hasPhotos, currentImageIndex, photos.length]
  );

  // Get clinic name from job data - Using useCallback like React Native
  const getName = useCallback((): string => {
    if (!job) return "Dermatologist Clinic";
    return job.title || "Dermatologist Clinic";
  }, [job]);

  // Get location string from job data - Using useCallback like React Native
  const getLocation = useCallback((): string => {
    if (!job) return "Location";
    return job.location || job.description || "Location";
  }, [job]);

  // Get distance string from job data - Using useCallback like React Native
  const getDistance = useCallback((): string => {
    if (!job) return "";
    if (job.distance !== undefined && job.distance !== null) {
      const distanceNum = Number(job.distance);
      if (!isNaN(distanceNum)) {
        return `${distanceNum.toFixed(1)} km away`;
      }
    }
    return "";
  }, [job]);

  // Get description - Using useCallback like React Native
  const getDescription = useCallback((): string => {
    if (!job) return "Professional dermatology care services";
    const jobId = job.id || "dermatologist_1";
    return (
      DERMATOLOGIST_DESCRIPTIONS_MAP[jobId] ||
      job.description ||
      "Professional dermatology care services"
    );
  }, [job]);

  // Get rating from job data - Using useCallback like React Native
  const getRating = useCallback((): number | null => {
    if (!job) return null;
    const jobData = job.jobData as any;
    return jobData?.rating || null;
  }, [job]);

  // Get user ratings total from job data - Using useCallback like React Native
  const getUserRatingsTotal = useCallback((): number | null => {
    if (!job) return null;
    const jobData = job.jobData as any;
    return jobData?.user_ratings_total || null;
  }, [job]);

  // Get opening hours status from job data - Using useCallback like React Native
  const getOpeningStatus = useCallback((): string | null => {
    if (!job) return null;
    const jobData = job.jobData as any;
    const isOpen = jobData?.opening_hours?.open_now;

    if (isOpen === undefined || isOpen === null) {
      return null;
    }

    return isOpen ? "Open Now" : "Closed";
  }, [job]);

  // Get phone number for the dermatologist clinic - Using useCallback like React Native
  const getPhoneNumber = useCallback((): string | null => {
    if (!job) return null;
    const jobId = job.id || "";
    return PHONE_NUMBERS_MAP[jobId] || null;
  }, [job]);

  // Handle call button press - Opens phone dialer
  const handleCall = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!job) return;

      const phoneNumber = getPhoneNumber();
      const name = getName();

      if (!phoneNumber) {
        alert(`No contact number available for ${name}.`);
        return;
      }

      // Format phone number for tel: URL (remove all non-numeric characters)
      const formattedNumber = phoneNumber.replace(/[^0-9+]/g, "");
      const telUrl = `tel:${formattedNumber}`;

      window.location.href = telUrl;
    },
    [job, getPhoneNumber, getName]
  );

  // Handle directions button press - Opens Maps app with exact location
  const handleDirections = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!job) return;

      const jobData = job.jobData as any;
      const lat = jobData?.geometry?.location?.lat;
      const lng = jobData?.geometry?.location?.lng;

      if (!lat || !lng) {
        alert("Unable to get location coordinates for directions.");
        return;
      }

      // Create Google Maps URL with exact coordinates and name
      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&destination_place_id=${job.id || ""}`;

      window.open(googleMapsUrl, "_blank");
    },
    [job]
  );

  // Handle image loading error - Using useCallback like React Native
  const handleImageError = useCallback(() => {
    console.warn(
      "⚠️ Failed to load image for dermatologist clinic:",
      job?.id || "unknown"
    );
    setImageError(true);
  }, [job]);

  // Compute derived values
  const rating = getRating();
  const userRatingsTotal = getUserRatingsTotal();
  const openingStatus = getOpeningStatus();
  const distance = getDistance();
  const description = getDescription();
  const visibleServices = DERMATOLOGIST_SERVICES.slice(0, 4);
  const moreServices =
    DERMATOLOGIST_SERVICES.length > 4 ? DERMATOLOGIST_SERVICES.length - 4 : 0;
  const hasPhoneNumber = getPhoneNumber() !== null;

  // Early return if job is not provided (after all hooks)
  if (!job) {
    return null;
  }

  return (
    <div
      onClick={() => onViewDetails(job)}
      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-200 cursor-pointer hover:scale-[0.99] mb-4"
    >
      {/* Image Carousel */}
      <div className="relative h-48 bg-gray-100">
        {currentPhoto && !imageError ? (
          <>
            <img
              src={currentPhoto}
              className="w-full h-full object-cover"
              alt={getName()}
              onError={handleImageError}
            />

            {/* Image Navigation Arrows */}
            {hasPhotos && photos.length > 1 && (
              <>
                {currentImageIndex > 0 && (
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 p-2 rounded-full text-white transition"
                  >
                    <ChevronLeft size={20} />
                  </button>
                )}

                {currentImageIndex < photos.length - 1 && (
                  <button
                    onClick={handleNextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 p-2 rounded-full text-white transition"
                  >
                    <ChevronRight size={20} />
                  </button>
                )}

                {/* Image Counter */}
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs font-semibold px-2 py-1 rounded-lg">
                  {currentImageIndex + 1} / {photos.length}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-200">
            <span className="text-6xl">💆</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3.5">
        {/* Clinic Name */}
        <h3 className="text-[17px] font-bold text-gray-900 mb-1.5 leading-snug line-clamp-2">
          {getName()}
        </h3>

        {/* Location */}
        <div className="flex items-center text-gray-500 mb-1">
          <MapPin size={14} className="mr-1 flex-shrink-0" />
          <span className="text-[13px] line-clamp-1">{getLocation()}</span>
        </div>

        {/* Distance */}
        {distance && (
          <p className="text-xs font-semibold text-green-600 mb-2">{distance}</p>
        )}

        {/* Description */}
        <p className="text-[13px] text-gray-600 leading-relaxed mb-2.5 line-clamp-3">
          {description}
        </p>

        {/* Category Badge */}
        <div className="inline-flex items-center gap-1 bg-pink-100 text-pink-600 text-[11px] font-semibold px-2 py-1 rounded-xl mb-2">
          <span className="text-sm">💆</span>
          <span>Dermatologist</span>
        </div>

        {/* Rating and Status Row */}
        <div className="flex items-center gap-2 mb-2.5">
          {rating && (
            <div className="flex items-center gap-1">
              <Star size={13} className="text-yellow-400 fill-yellow-400" />
              <span className="text-[13px] font-semibold text-gray-900">
                {rating.toFixed(1)}
              </span>
              {userRatingsTotal && (
                <span className="text-xs text-gray-500">
                  ({userRatingsTotal})
                </span>
              )}
            </div>
          )}

          {openingStatus && (
            <div
              className={`flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded ${openingStatus.includes("Open")
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
                }`}
            >
              <Clock size={11} />
              <span>{openingStatus}</span>
            </div>
          )}
        </div>

        {/* Services */}
        <div className="mb-3">
          <p className="text-[10px] font-bold text-gray-500 tracking-wide mb-1.5">
            SERVICES:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {visibleServices.map((service, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 bg-pink-100 text-pink-600 text-[11px] font-medium px-2 py-1 rounded"
              >
                <CheckCircle size={11} />
                <span>{service}</span>
              </span>
            ))}
            {moreServices > 0 && (
              <span className="inline-flex items-center bg-gray-100 text-gray-600 text-[11px] font-medium px-2 py-1 rounded">
                +{moreServices} more
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={handleDirections}
            className="flex-1 flex items-center justify-center gap-1 border-2 border-indigo-600 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-bold text-xs py-2.5 rounded-lg transition-all active:scale-95"
          >
            <Navigation size={14} />
            <span>Directions</span>
          </button>

          <button
            onClick={handleCall}
            disabled={!hasPhoneNumber}
            className={`flex-1 flex items-center justify-center gap-1 border-2 font-bold text-xs py-2.5 rounded-lg transition-all active:scale-95 ${hasPhoneNumber
                ? "border-green-600 bg-green-50 text-green-600 hover:bg-green-100"
                : "border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
          >
            <Phone size={14} />
            <span>Call</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Component - displays grid of dummy data or single card
const NearbyDermatologistsCard: React.FC<NearbyDermatologistsCardProps> = (props) => {
  // If no job is provided, render the grid of dummy dermatologists
  if (!props.job) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
        {DUMMY_DERMATOLOGISTS.map((dermatologist) => (
          <SingleDermatologistCard
            key={dermatologist.place_id}
            job={{
              id: dermatologist.place_id,
              title: dermatologist.name,
              location: dermatologist.vicinity,
              distance: dermatologist.distance,
              category: "Dermatologist",
              jobData: {
                rating: dermatologist.rating,
                user_ratings_total: dermatologist.user_ratings_total,
                opening_hours: dermatologist.opening_hours,
                geometry: dermatologist.geometry,
                business_status: dermatologist.business_status,
                price_level: dermatologist.price_level,
                types: dermatologist.types,
              },
            }}
            onViewDetails={props.onViewDetails}
          />
        ))}
      </div>
    );
  }

  // If job is provided, render individual card
  return (
    <SingleDermatologistCard job={props.job} onViewDetails={props.onViewDetails} />
  );
};

export default NearbyDermatologistsCard;
